import { createServerFn } from "@tanstack/react-start";
import { STATES } from "@/data/states";
import { ALL_GAMES, type Game } from "@/data/games";

const API_URL =
  "https://rapidcdn.top/lotterydata/ashley@fwmedia.io/dqerqwer/lottery.php";

export interface LiveGame {
  api_game_id: string;
  api_game_name: string;
  state_abbr: string; // stateprov_id (e.g. "AZ", "AC")
  state_name: string;
  country: string;
  update_time: string;
  last_draw_date: string | null; // ISO YYYY-MM-DD
  last_draw_raw: string | null;
  winning_numbers: number[];
  bonus_numbers: number[];
  multiplier: number | null;
  next_draw_date: string | null; // ISO YYYY-MM-DD
  next_draw_top_prize: number | null;
  jackpot_amount: number | null;
  jackpot_date: string | null;
}

export interface LiveLotteryData {
  fetched_at: string;
  games: LiveGame[];
}

function parseMDY(d?: string | null): string | null {
  if (!d) return null;
  const m = d.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (!m) return null;
  return `${m[3]}-${m[1]}-${m[2]}`;
}

function parseDrawNumbers(raw: string): {
  winning: number[];
  bonus: number[];
  multiplier: number | null;
} {
  // e.g. "12-31-38-60-66, Powerball: 14, Power Play: 2"
  // or "09-30-36-38-40, Mega Ball: 03"
  // or "5-2-8" or "11-13-31-38-39"
  const parts = raw.split(",").map((p) => p.trim());
  const winning: number[] = [];
  const bonus: number[] = [];
  let multiplier: number | null = null;

  if (parts.length > 0) {
    const nums = parts[0]
      .split("-")
      .map((n) => parseInt(n, 10))
      .filter((n) => !Number.isNaN(n));
    winning.push(...nums);
  }

  for (let i = 1; i < parts.length; i++) {
    const seg = parts[i];
    const [labelRaw, valRaw] = seg.split(":").map((s) => s?.trim() ?? "");
    const label = labelRaw.toLowerCase();
    if (!valRaw) continue;
    if (/play|multiplier|megaplier/.test(label)) {
      const m = parseInt(valRaw, 10);
      if (!Number.isNaN(m)) multiplier = m;
    } else {
      const nums = valRaw
        .split("-")
        .map((n) => parseInt(n, 10))
        .filter((n) => !Number.isNaN(n));
      bonus.push(...nums);
    }
  }
  return { winning, bonus, multiplier };
}

function parseXml(xml: string): LiveGame[] {
  const games: LiveGame[] = [];
  const stateRe =
    /<StateProv\s+stateprov_name="([^"]+)"\s+stateprov_id="([^"]+)"\s+country="([^"]+)">([\s\S]*?)<\/StateProv>/g;
  let sm: RegExpExecArray | null;
  while ((sm = stateRe.exec(xml)) !== null) {
    const stateName = sm[1];
    const stateAbbr = sm[2];
    const country = sm[3];
    const body = sm[4];
    const gameRe =
      /<game\s+game_id="([^"]+)"\s+game_name="([^"]+)"\s+update_time="([^"]+)">([\s\S]*?)<\/game>/g;
    let gm: RegExpExecArray | null;
    while ((gm = gameRe.exec(body)) !== null) {
      const apiId = gm[1];
      const apiName = gm[2];
      const updateTime = gm[3];
      const inner = gm[4];

      const lastDrawDate = inner.match(/<lastdraw_date>([^<]+)<\/lastdraw_date>/)?.[1] ?? null;
      const lastDrawRaw =
        inner.match(/<lastdraw_numbers>([^<]+)<\/lastdraw_numbers>/)?.[1] ?? null;
      const nextDrawMatch = inner.match(
        /<nextdraw_date(?:\s+top_prize='([^']+)')?>([^<]+)<\/nextdraw_date>/,
      );
      const nextDrawDate = nextDrawMatch?.[2] ?? null;
      const nextTopPrize = nextDrawMatch?.[1] ? parseFloat(nextDrawMatch[1]) : null;
      const jackpotMatch = inner.match(/<jackpot(?:\s+date="([^"]+)")?>([^<]+)<\/jackpot>/);
      const jackpot = jackpotMatch ? parseFloat(jackpotMatch[2]) : null;
      const jackpotDate = jackpotMatch?.[1] ? parseMDY(jackpotMatch[1]) : null;

      const parsed = lastDrawRaw
        ? parseDrawNumbers(lastDrawRaw)
        : { winning: [], bonus: [], multiplier: null };

      games.push({
        api_game_id: apiId,
        api_game_name: apiName,
        state_abbr: stateAbbr,
        state_name: stateName,
        country,
        update_time: updateTime,
        last_draw_date: parseMDY(lastDrawDate),
        last_draw_raw: lastDrawRaw,
        winning_numbers: parsed.winning,
        bonus_numbers: parsed.bonus,
        multiplier: parsed.multiplier,
        next_draw_date: parseMDY(nextDrawDate),
        next_draw_top_prize: nextTopPrize,
        jackpot_amount: jackpot,
        jackpot_date: jackpotDate,
      });
    }
  }
  return games;
}

// Cached in-process with stale-while-revalidate so only one request per worker
// per TTL ever waits on the upstream feed; everyone else is served instantly.
const TTL_MS = 5 * 60 * 1000;
const SWR_MS = 30 * 60 * 1000;

export const fetchLiveLottery = createServerFn({ method: "GET" }).handler(
  async (): Promise<LiveLotteryData> => {
    const { cached } = await import("@/lib/server-cache");
    try {
      return await cached(
        "live:lottery-feed",
        TTL_MS,
        async () => {
          const res = await fetch(API_URL, {
            headers: { Accept: "application/xml, text/xml, */*" },
          });
          if (!res.ok) throw new Error(`Lottery API HTTP ${res.status}`);
          const xml = await res.text();
          return { fetched_at: new Date().toISOString(), games: parseXml(xml) } satisfies LiveLotteryData;
        },
        { staleWhileRevalidateMs: SWR_MS },
      );
    } catch (err) {
      console.error("[lottery-api] fetch failed:", err);
      return { fetched_at: new Date().toISOString(), games: [] };
    }
  },
);

// ============ Mapping live data → internal game IDs ============

const MULTISTATE_NAME_TO_SLUG: Record<string, string> = {
  powerball: "powerball",
  "mega millions": "mega-millions",
  "lucky for life": "lucky-for-life",
  cash4life: "cash4life",
  "cash 4 life": "cash4life",
  "lotto america": "lotto-america",
  "2by2": "2by2",
  "2 by 2": "2by2",
  "millionaire for life": "millionaire-for-life",
};

function normalizeName(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** Find our Game record best matching a live API game entry. */
export function matchGame(live: LiveGame): Game | null {
  const apiName = normalizeName(live.api_game_name);

  // Multistate match by name
  const msSlug = MULTISTATE_NAME_TO_SLUG[apiName];
  if (msSlug) {
    const g = ALL_GAMES.find((x) => x.slug === msSlug && x.is_multistate);
    if (g) return g;
  }

  // State-specific: find state, then match game name within it
  const state = STATES.find((s) => s.abbreviation === live.state_abbr);
  if (!state) return null;

  const stateGames = ALL_GAMES.filter((g) => g.state_id === state.id);
  if (stateGames.length === 0) return null;

  // Exact-ish match by normalized name (strip state name prefix from our slug)
  for (const g of stateGames) {
    const ourName = normalizeName(g.name);
    if (ourName === apiName) return g;
  }
  // Contains match
  for (const g of stateGames) {
    const ourName = normalizeName(g.name);
    if (ourName.includes(apiName) || apiName.includes(ourName)) return g;
  }
  return null;
}

export interface LiveByGameMaps {
  jackpotByGameId: Record<
    string,
    { jackpot_amount: number; cash_value: number | null; next_draw_iso: string }
  >;
  latestByGameId: Record<
    string,
    {
      draw_date: string;
      winning_numbers: number[];
      bonus_numbers: number[];
      multiplier: number | null;
    }
  >;
  syntheticGames: Array<{
    game: Game;
    jackpot?: { jackpot_amount: number; cash_value: number | null; next_draw_iso: string };
    latest?: {
      draw_date: string;
      winning_numbers: number[];
      bonus_numbers: number[];
      multiplier: number | null;
    };
  }>;
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function synthesizeGame(live: LiveGame): Game | null {
  const state = STATES.find((s) => s.abbreviation === live.state_abbr);
  // Skip Canadian provinces and non-lottery states
  if (!state || !state.has_lottery) return null;

  const isMs = MULTISTATE_NAME_TO_SLUG[normalizeName(live.api_game_name)] !== undefined;
  const slug = isMs ? slugify(live.api_game_name) : `${state.slug}-${slugify(live.api_game_name)}`;
  const id = isMs ? `game-${slug}` : `game-${state.slug}-${slugify(live.api_game_name)}`;

  return {
    id,
    name: live.api_game_name,
    slug,
    is_multistate: isMs,
    state_id: isMs ? null : state.id,
    number_count_main: live.winning_numbers.length || 5,
    number_range_main: Math.max(...live.winning_numbers, 50),
    number_count_bonus: live.bonus_numbers.length,
    number_range_bonus: live.bonus_numbers.length ? Math.max(...live.bonus_numbers, 25) : 0,
    has_multiplier: live.multiplier !== null,
    ticket_price: 1,
    draw_schedule: "See lottery site for schedule",
  };
}

/** Build O(1) lookup maps from live data, keyed by our internal Game.id. */
export function buildLiveMaps(data: LiveLotteryData): LiveByGameMaps {
  const jackpotByGameId: LiveByGameMaps["jackpotByGameId"] = {};
  const latestByGameId: LiveByGameMaps["latestByGameId"] = {};
  const synthMap = new Map<
    string,
    {
      game: Game;
      jackpot?: { jackpot_amount: number; cash_value: number | null; next_draw_iso: string };
      latest?: {
        draw_date: string;
        winning_numbers: number[];
        bonus_numbers: number[];
        multiplier: number | null;
      };
    }
  >();

  for (const live of data.games) {
    const matched = matchGame(live);
    const g = matched ?? synthesizeGame(live);
    if (!g) continue;

    const drawIso = live.next_draw_date
      ? new Date(`${live.next_draw_date}T22:59:00Z`).toISOString()
      : new Date().toISOString();

    if (live.jackpot_amount && live.jackpot_amount > 0) {
      if (!jackpotByGameId[g.id]) {
        jackpotByGameId[g.id] = {
          jackpot_amount: live.jackpot_amount,
          cash_value: null,
          next_draw_iso: drawIso,
        };
      }
    }

    if (live.last_draw_date && live.winning_numbers.length > 0) {
      const existing = latestByGameId[g.id];
      if (!existing || existing.draw_date < live.last_draw_date) {
        latestByGameId[g.id] = {
          draw_date: live.last_draw_date,
          winning_numbers: live.winning_numbers,
          bonus_numbers: live.bonus_numbers,
          multiplier: live.multiplier,
        };
      }
    }

    // Track synthetic (non-matched) games so the UI can list them too
    if (!matched) {
      const existing = synthMap.get(g.id);
      const jp = jackpotByGameId[g.id];
      const lt = latestByGameId[g.id];
      synthMap.set(g.id, {
        game: g,
        jackpot: jp ?? existing?.jackpot,
        latest: lt ?? existing?.latest,
      });
    }
  }

  return { jackpotByGameId, latestByGameId, syntheticGames: Array.from(synthMap.values()) };
}